import {
  Component,
  Inject,
  input,
  OnInit,
  output,
  signal,
  computed,
} from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DIALOG } from '@progress/kendo-angular-dialog';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { FormsModule } from '@angular/forms';
import { KENDO_DROPDOWNLIST } from '@progress/kendo-angular-dropdowns';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import {
  DEFAULT_OPENAI_MODEL,
  EmailEntryType,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookAbEntryDetails,
  OutlookLeadSearch,
  OutlookTimelineSummaryArgs,
} from '@maximizer/outlook/shared/domain';
import { Octopus, CDN_URL, OpenAI } from '@maximizer/core/shared/domain';
import { MenuItem, OUTLOOK_SHARED_UI } from '@maximizer/outlook/shared/ui';
import {
  Helper,
  SafeHtmlPipe,
  Summary,
  TimelineSummaryHtmlBuilder,
  TimelineSummaryPrompt,
} from '@maximizer/ai/ui';
import { TimelineService } from '@maximizer/outlook/timeline/data-access';
import {
  ConfigurationService,
  GlobalStore,
  OpenAiService,
} from '@maximizer/core/shared/data-access';
import {
  CODE_ADMIN,
  IqBoostPromptLookupResult,
  MaximizerAiService,
  PIIContext,
  PIIContactRecord,
  PiiAnonymizationService,
  SystemType,
} from '@maximizer/ai/data-access';
import { escapeHtml } from '@maximizer/shared/util';
import {
  EntryStore,
  OutlookStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  DATA_PROCESSING_SETTING_CODES,
  OutlookAiApiService,
  parseAdminDataProcessingFeatures,
  resolveAnonymizationEnabled,
} from '@maximizer/outlook/ai/data-access';
import {
  catchError,
  finalize,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { OutlookAiResponseActionsComponent } from '../response-actions/response-actions.component';
import { KENDO_TOOLTIP } from '@progress/kendo-angular-tooltip';

const TIMELINE_SUMMARY_PROMPT_NAME = 'timeline-summary';
const SYSTEM_PROMPT_TAG = 'system';

@Component({
  selector: 'maximizer-outlook-ai-timeline-summary',
  templateUrl: './timeline-summary.component.html',
  imports: [
    FormsModule,
    SafeHtmlPipe,
    TranslatePipe,
    KENDO_BUTTON,
    KENDO_DIALOG,
    UI_LOADER,
    KENDO_DROPDOWNLIST,
    KENDO_LABEL,
    OUTLOOK_SHARED_UI,
    OutlookAiResponseActionsComponent,
    KENDO_TOOLTIP,
  ],
})
export class OutlookAiTimelineSummary implements OnInit {
  timelineSummaryArgs = input<OutlookTimelineSummaryArgs>();
  closeDialog = output<void>();

  private readonly defaultDateRangeId = Octopus.DateOptionId.THISANDLASTMONTH;
  protected readonly aiTimelineSummaryModule = EventModule.AiTimelineSummary;
  protected readonly anonymizerEnabled = signal(false);

  // Starts true so the dialog opens into the loader; the empty state must
  // only appear after a summarize attempt has finished.
  protected loading = signal(true);
  protected name = signal('');
  protected summaryResults = signal<string>('');
  protected feedbackGiven = signal(false);

  private trackingType: string = EntryType.Contact;
  private entryContext = '';
  protected selectedDateRangeId = this.defaultDateRangeId;

  protected deAnonymizedSummary = computed(() =>
    this.aiApiService.deAnonymizeHtml(this.summaryResults()),
  );

  protected displaySummary = computed(() => {
    const html = this.summaryResults();
    if (!html) return '';
    if (!this.anonymizerEnabled())
      return this.aiApiService.deAnonymizeHtml(html);

    const notSharedToAI = this.translate.instant(
      'outlook.iqBoost.anonymization.notSharedToAI',
    );
    const anonymizedLabel = this.translate.instant(
      'outlook.iqBoost.anonymization.anonymized',
    );
    return PiiAnonymizationService.processPiiMaskTokensInHtml(
      html,
      (type, number) => {
        const mask = `${type}${number}`;
        const original = this.aiApiService.deAnonymize(mask);
        const displayText = original ?? anonymizedLabel;
        return `<span
        class="inline-block bg-transparent rounded px-1 m-[1px] border border-black font-normal text-black"
        title="${escapeHtml(notSharedToAI)}"
        data-type="${type.toLowerCase()}"
        data-number="${number.toLowerCase()}"
      ><i class="fa-icons icon-lock-keyhole icon-14 text-black pr-1"></i>${escapeHtml(displayText)}</span>`;
      },
    );
  });

  private readonly hasActiveFilter = computed(() => {
    const filter = this.timelineSummaryArgs()?.filter;
    return !!filter?.interactions.length || !!filter?.users.length;
  });

  private readonly queryEntryType = computed<
    EntryType.AddressBook | EntryType.Lead
  >(() =>
    this.entryStore.type() === EntryType.Lead
      ? EntryType.Lead
      : EntryType.AddressBook,
  );

  protected readonly dateRanges: MenuItem[] = [
    {
      id: Octopus.DateOptionId.THISWEEK,
      text: 'outlook.summarization.dateRange.thisWeek',
    },
    {
      id: Octopus.DateOptionId.LASTWEEK,
      text: 'outlook.summarization.dateRange.lastWeek',
    },
    {
      id: Octopus.DateOptionId.THISMONTH,
      text: 'outlook.summarization.dateRange.thisMonth',
    },
    {
      id: Octopus.DateOptionId.THISANDLASTMONTH,
      text: 'outlook.summarization.dateRange.thisAndLastMonth',
    },
    {
      id: Octopus.DateOptionId.LASTMONTH,
      text: 'outlook.summarization.dateRange.lastMonth',
    },
    {
      id: Octopus.DateOptionId.LASTTHREEMONTHS,
      text: 'outlook.summarization.dateRange.last3Months',
    },
    {
      id: Octopus.DateOptionId.LASTSIXMONTHS,
      text: 'outlook.summarization.dateRange.last6Months',
    },
  ];

  constructor(
    @Inject(CDN_URL) protected readonly cdn: string,
    private readonly timelineService: TimelineService,
    private readonly maximizerAi: MaximizerAiService,
    private readonly openAi: OpenAiService,
    private readonly translate: TranslateService,
    private readonly tracker: TrackerService,
    private readonly entryStore: EntryStore,
    private readonly globalStore: GlobalStore,
    private readonly outlookStore: OutlookStore,
    private readonly aiApiService: OutlookAiApiService,
    private readonly configurationService: ConfigurationService,
  ) {}

  ngOnInit(): void {
    this.populateEntryContext();
    this.loadAnonymizationSetting()
      .pipe(take(1))
      .subscribe(() => this.generateSummary());
  }

  private loadAnonymizationSetting(): Observable<void> {
    const userId = this.globalStore.session()?.user.id ?? '';
    const userSetting$ = this.configurationService
      .getSetting({ ...DATA_PROCESSING_SETTING_CODES, code2: userId })
      .pipe(catchError(() => of(null)));
    const adminSetting$ = this.configurationService
      .getSetting({ ...DATA_PROCESSING_SETTING_CODES, code2: CODE_ADMIN })
      .pipe(catchError(() => of(null)));

    return forkJoin({
      userSetting: userSetting$,
      adminSetting: adminSetting$,
    }).pipe(
      tap(({ userSetting, adminSetting }) => {
        const adminFeatures =
          parseAdminDataProcessingFeatures(adminSetting) ??
          this.outlookStore.adminDataProcessingFeatures();
        this.anonymizerEnabled.set(
          resolveAnonymizationEnabled(userSetting, adminFeatures),
        );
      }),
      map(() => void 0),
    );
  }

  private populateEntryContext(): void {
    const entry = this.entryStore.entry();
    const type = this.entryStore.type();

    if (!entry) {
      this.trackingType =
        type === EntryType.Lead ? EntryType.Lead : EntryType.Contact;
      this.entryContext = `Entry type: ${this.entryStore.type()}`;
      return;
    }

    this.assignEntryContext(entry, type);
  }

  private assignEntryContext(
    entry: OutlookAbEntryDetails | OutlookLeadSearch,
    type: EmailEntryType,
  ): void {
    if (type === EntryType.Lead) {
      const lead = entry as OutlookLeadSearch;
      this.trackingType = EntryType.Lead;
      this.name.set(lead.name ?? '');
      this.entryContext = this.getLeadStringContext(lead);
      return;
    }

    const abEntry = entry as OutlookAbEntryDetails;
    this.trackingType = abEntry.type || EntryType.Contact;
    this.name.set(abEntry.name ?? '');
    this.entryContext = this.getAbEntryStringContext(abEntry);
  }

  protected generateSummary(): void {
    const args = this.timelineSummaryArgs();
    if (!args?.key || !this.hasActiveFilter()) {
      this.summaryResults.set('');
      this.loading.set(false);
      return;
    }

    this.feedbackGiven.set(false);
    this.loading.set(true);

    const dateOffset = Octopus.getDateOffset(this.selectedDateRangeId);
    const customDateQuery: Octopus.ComparisonValue = Array.isArray(dateOffset)
      ? { $RANGE: dateOffset }
      : { $EQ: dateOffset };
    let success = true;

    forkJoin({
      events: this.timelineService.forEntry(args.key, this.queryEntryType(), {
        ...args.filter,
        customDateQuery,
      }),
      summaryPrompt: this.loadTimelineSummaryPrompt(),
      systemPrompts: this.loadSystemPrompts(),
    })
      .pipe(
        switchMap(({ events, summaryPrompt, systemPrompts }) => {
          const timelineTexts = this.mapTimelineEvents(events.data);
          if (timelineTexts.length) {
            const config =
              (summaryPrompt.config as OpenAI.RequestConfiguration) ?? {};
            const languagePrompt = Helper.getLanguageSuggestedPrompt(
              this.findSystemPrompt(systemPrompts, SystemType.CHANGE_LANGUAGE),
              this.outlookStore.language(),
            );
            const anonymizedConsiderationsPrompt = this.anonymizerEnabled()
              ? this.findSystemPrompt(
                  systemPrompts,
                  SystemType.ANONYMIZED_CONSIDERATIONS,
                )?.prompt
              : undefined;

            const requestConfig: OpenAI.RequestConfiguration = {
              model: config.model ?? DEFAULT_OPENAI_MODEL,
              max_tokens: config.max_tokens ?? 700,
              temperature: config.temperature ?? 0.1,
              response_format: { type: 'json_object' },
            };

            const systemParts = [
              anonymizedConsiderationsPrompt ?? '',
              languagePrompt ?? '',
              summaryPrompt.prompt,
            ].filter((p) => p.length > 0);
            const finalPrompt = systemParts.join('\n\n');

            const contextMessages: OpenAI.GPTMessage[] = [
              { role: 'user', content: this.entryContext },
            ];
            const userMessages: OpenAI.GPTMessage[] = timelineTexts.map(
              (content) => ({ role: 'user', content }),
            );

            return this.aiApiService
              .anonymizeContextAndUser(
                this.anonymizerEnabled(),
                contextMessages,
                userMessages,
                [],
                this.getPiiContext(),
              )
              .pipe(
                switchMap(({ context, user }) =>
                  this.openAi.summarize(
                    context.map((m) => m.content),
                    user.map((m) => m.content),
                    finalPrompt,
                    requestConfig,
                  ),
                ),
              );
          }
          return of(null);
        }),
        map((summary) => this.mapSummaryHtml(summary)),
        catchError(() => {
          success = false;
          return of('');
        }),
        finalize(() => {
          this.loading.set(false);
          this.tracker.trackEvent({
            module: EventModule.AiTimelineSummary,
            journey: EventJourney.Summarize,
            action: EventAction.Completed,
            metadata: {
              result: success ? EventMetadata.Success : EventMetadata.Failed,
              entry: this.trackingType,
              dateRange: this.dateRangeTrackingName(this.selectedDateRangeId),
            },
          });
        }),
      )
      .subscribe((summaryHtml) => this.summaryResults.set(summaryHtml));
  }

  private loadTimelineSummaryPrompt(): Observable<{
    prompt: string;
    config?: unknown;
  }> {
    return this.maximizerAi
      .lookupPrompts({
        project: 'MaximizerAI',
        filter: { name: [TIMELINE_SUMMARY_PROMPT_NAME] },
      })
      .pipe(
        map((lookupResults) =>
          lookupResults.length
            ? lookupResults[0]
            : { prompt: TimelineSummaryPrompt },
        ),
        catchError(() =>
          of({ prompt: TimelineSummaryPrompt } as {
            prompt: string;
            config?: unknown;
          }),
        ),
      );
  }

  private loadSystemPrompts(): Observable<IqBoostPromptLookupResult[]> {
    return this.maximizerAi
      .lookupPrompts({
        project: 'MaximizerAI',
        filter: { tag: [SYSTEM_PROMPT_TAG] },
      })
      .pipe(catchError(() => of([] as IqBoostPromptLookupResult[])));
  }

  private findSystemPrompt(
    systemPrompts: IqBoostPromptLookupResult[],
    systemType: SystemType,
  ): IqBoostPromptLookupResult | undefined {
    return systemPrompts.find(
      (systemPrompt) =>
        systemPrompt.config?.maximizer?.systemType === systemType,
    );
  }

  private dateRangeTrackingName(dateRangeId: Octopus.DateOptionId): string {
    return dateRangeId.slice(1).toLowerCase();
  }

  private mapTimelineEvents(
    events: Array<{
      type: string;
      subject?: string;
      description?: string;
      date?: { display?: string };
      creator?: { display?: string };
      entriesDisplay?: string;
    }>,
  ): string[] {
    return events
      .map((event) => {
        const lines = [
          `Type: ${event.type}`,
          event.date?.display ? `Date: ${event.date.display}` : '',
          event.subject ? `Subject: ${event.subject}` : '',
          event.description ? `Description: ${event.description}` : '',
          event.entriesDisplay ? `With: ${event.entriesDisplay}` : '',
          event.creator?.display ? `Created by: ${event.creator.display}` : '',
        ].filter((line) => line.length > 0);

        return lines.join('\n');
      })
      .filter((eventText) => eventText.length > 0);
  }

  private mapSummaryHtml(summary: unknown): string {
    if (!summary || typeof summary !== 'object') {
      return '';
    }

    return new TimelineSummaryHtmlBuilder(
      summary as Summary,
      this.translate,
      'outlook',
    )
      .dealContext()
      .keyMilestoneAndTimelines()
      .highlightsOfPastCommunications()
      .recentCommunicationsSummary()
      .outstandingItemsAndRisks()
      .recommendedActions()
      .sentimentAnalysis()
      .build();
  }

  protected onDateRangeChange(dateRange: Octopus.DateOptionId): void {
    this.selectedDateRangeId = dateRange;
  }

  private getAbEntryStringContext(abEntry: OutlookAbEntryDetails): string {
    return [
      abEntry.type ? `Type: "${abEntry.type}"` : '',
      abEntry.name ? `Name: "${abEntry.name}"` : '',
      abEntry.type?.toLowerCase() !== 'company' && abEntry.companyName
        ? `Company: "${abEntry.companyName}"`
        : '',
      abEntry.position ? `Position: "${abEntry.position}"` : '',
      abEntry.lastContactDateValue
        ? `Last Contact: "${abEntry.lastContactDateValue}"`
        : '',
    ]
      .filter((line) => line.length > 0)
      .join('\n');
  }

  private getLeadStringContext(lead: OutlookLeadSearch): string {
    return [
      `Type: "Lead"`,
      lead.name ? `Name: "${lead.name}"` : '',
      lead.companyName ? `Company: "${lead.companyName}"` : '',
      lead.position ? `Position: "${lead.position}"` : '',
    ]
      .filter((line) => line.length > 0)
      .join('\n');
  }

  private getPiiContext(): PIIContext {
    const entry = this.entryStore.entry();
    const session = this.globalStore.session();
    const contacts: PIIContactRecord[] = [];

    if (entry) {
      const abEntry = entry as OutlookAbEntryDetails;
      contacts.push({
        firstName: abEntry.firstName ?? undefined,
        lastName: abEntry.lastName ?? undefined,
        fullName: abEntry.name ?? undefined,
        email: abEntry.emails?.[0]?.value ?? undefined,
        phone: abEntry.phones?.[0]?.value ?? undefined,
      });
    }

    contacts.push({
      firstName: session?.user?.firstName ?? undefined,
      lastName: session?.user?.lastName ?? undefined,
      fullName: session?.user?.displayName ?? undefined,
      email: session?.user?.email1?.email ?? undefined,
      phone: session?.user?.phone1?.phoneNumber ?? undefined,
    });

    return { contacts };
  }
}
